/* eslint-disable @next/next/no-img-element */
import React, { useState } from 'react'
import styled from 'styled-components'
import theme from 'stories/utils/theme'
import { Body1, Headline4, QuoteText, Subtitle1 } from 'stories/elements/Typography/Text'
import { PrismicImage, TextTestimonialCarouselSlice } from 'interfaces/GeneratedLandingPage'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import useMedia from 'lib/hooks/useMedia'
import { useToggleState } from 'lib/hooks/useToggleState'
import { useTransition } from 'react-spring'
import { CarouselCard, CarouselIconButton } from './CarouselComponent.css'
import ArrowBackIcon from 'assets/icons/prev-carousel-arrow-blue.svg'
import ArrowForwardIcon from 'assets/icons/next-carousel-arrow-blue.svg'

interface TextTestimonialCarouselProps {
    data: TextTestimonialCarouselSlice['items']
    backgroundColor: string
    backgroundImage: PrismicImage
}

const Container = styled.div<{ backgroundColor?: string }>`
    display: flex;
    width: 100%;
    align-self: center;
    justify-self: center;
    background-color: ${({ backgroundColor }) => backgroundColor ?? theme.colors.accentBlue};
    background-repeat: no-repeat;
    object-position: right top;
    object-fit: cover;
    height: 100%;
    min-height: 800px;
    padding: 0 5%;
    overflow: hidden;
    @media (max-width: 750px) {
        padding: 0 25px;
        height: 600px;
        button {
            display: none;
        }
    }
`

const Wrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    transform: translateZ(0);
`

const Card = styled.div`
    position: relative;
    max-width: 1000px;
    overflow: hidden;
    min-height: 450px;
    background-color: ${theme.colors.accentBlue};
    border: 3px solid ${theme.colors.accentBlue};
    box-shadow: 4px 4px 0px ${theme.colors.accentBlue};
    border-radius: ${theme.borderRadius.normal};
    display: grid;
    grid-template-columns: 1fr;

    padding: 75px 50px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    background-color: ${theme.colors.white};
    img {
        position: absolute;
        bottom: 30px;
        right: 50px;
    }
    @media (max-width: 1050px) {
        width: 600px;
        min-height: 550px;
    }
    @media (max-width: 750px) {
        width: 100%;
        min-height: 650px;
        padding: 25px;
        h4,
        h5,
        p {
            text-align: center;
        }
        img {
            bottom: 25px;
            right: 25px;
        }
    }
    @media (max-width: 400px) {
        min-height: 600px;
        p {
            font-size: 16px;
            line-height: 21.79px;
        }
    }
`

export default function TextTestimonialCarousel({
    data: testimonials,
    backgroundColor,
    backgroundImage,
}: TextTestimonialCarouselProps) {
    const [count, setCount] = useState(0)
    const [direction, toggleDirection] = useToggleState(false)
    const md = useMedia(deviceSize.md)
    const transitions = useTransition([count], (item) => item, {
        from: {
            transform: `translate3d(${direction ? '' : '-'}${md ? 0 : '25%'},0,0)`,
            opacity: 0,
            position: 'absolute',
        },
        enter: { transform: 'translate3d(0,0,0)', opacity: 1 },
        leave: {
            opacity: 0,
            transform: `translate3d(${direction ? '-' : ''}${md ? 0 : '25%'},0,0)`,
        },
    })

    const prevSlide = () => {
        if (direction) toggleDirection()
        const prevSlide = count - 1 < 0 ? testimonials.length - 1 : count - 1
        setCount(prevSlide)
    }

    const nextSlide = () => {
        if (!direction) toggleDirection()
        const nextSlide = count + 1 < testimonials.length ? count + 1 : 0
        setCount(nextSlide)
    }

    return (
        <Container backgroundColor={backgroundColor} style={{ backgroundImage: `url(${backgroundImage?.url ?? ''})` }}>
            {!md ? (
                <CarouselIconButton onClick={prevSlide} isLeft revertColors>
                    <div className="circle">
                        <ArrowBackIcon />
                    </div>
                </CarouselIconButton>
            ) : null}
            <Wrapper>
                {transitions.map(({ item, props, key }) => {
                    const data = testimonials[item]
                    const isFooterInfoPresent = !!data.footer_info

                    return (
                        <CarouselCard key={`testimonial-${key}`} style={props}>
                            <Card>
                                <div className="text">
                                    <Headline4 color={theme.colors.subtitleBlack}>{data.user_title}</Headline4>
                                    <Subtitle1 color={theme.colors.body2Black} marginBottom={50}>
                                        {data.user_subtitle}
                                    </Subtitle1>
                                    <QuoteText
                                        color={theme.colors.subtitleBlack}
                                        marginBottom={isFooterInfoPresent ? 25 : 0}
                                    >
                                        {data.text}
                                    </QuoteText>
                                    {isFooterInfoPresent ? <Body1>{data.footer_info}</Body1> : null}
                                </div>
                                <img
                                    src={data.company_logo?.url ?? ''}
                                    alt={data.company_logo?.alt ?? 'Company logo'}
                                    width={data.company_logo.dimensions?.width}
                                    height={data.company_logo.dimensions?.height}
                                    loading="lazy"
                                />
                            </Card>
                        </CarouselCard>
                    )
                })}
            </Wrapper>
            {!md ? (
                <CarouselIconButton onClick={nextSlide} revertColors>
                    <div className="circle">
                        <ArrowForwardIcon />
                    </div>
                </CarouselIconButton>
            ) : null}
        </Container>
    )
}
